/**
 * Dashboard Icon Utilities
 *
 * Thin wrappers over the canonical vocabulary in `@/lib/icon-vocabulary`
 * (#14985). This module no longer holds an icon map of its own: the names a
 * dashboard (or scorecard, or announcement) can pick and render are the same
 * names every other surface picks and renders. Legacy emoji and `bar-chart`
 * values resolve through the canonical module's aliases.
 */

import { LayoutDashboard, type LucideIcon } from '@/components/icons'
import { normalizeIconName, resolveIcon } from '@/lib/icon-vocabulary'

/**
 * Default icon when the value names nothing this build knows.
 */
export const DEFAULT_DASHBOARD_ICON = LayoutDashboard

/**
 * Get the Lucide icon component for a dashboard icon value.
 * Handles canonical names and every legacy alias (emoji, PascalCase).
 *
 * @returns The icon, {@link DEFAULT_DASHBOARD_ICON} for an unknown value, or
 *   null if value is null/undefined/empty
 */
export function getDashboardIcon(
  value: string | null | undefined,
): LucideIcon | null {
  if (!value) return null
  return resolveIcon(value) ?? DEFAULT_DASHBOARD_ICON
}

/**
 * The canonical icon name for a stored value (resolves emoji and other
 * legacy aliases), or null for an unknown or empty value.
 */
export function getIconName(value: string | null | undefined): string | null {
  return normalizeIconName(value)
}
